import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  LinearProgress,
  Chip,
  Alert,
  Tooltip,
  Divider
} from '@mui/material';
import {
  IoMic,
  IoMicOff,
  IoPlay,
  IoPause,
  IoStop,
  IoClose,
  IoSettings,
  IoCloudUpload,
  IoDocument
} from 'react-icons/io5';
import { useVoiceRecording } from '../../hooks/useVoiceRecording';
import { TOUCH_HIT_SX } from '../../theme/adminTheme';

const VoiceRecordingComponent = ({ 
  onTranscriptionComplete, 
  showMessage,
  onInsertText,
  onAutoSendMessage, // New prop for auto-sending messages
  compact = false 
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [autoSendCountdown, setAutoSendCountdown] = useState(0);
  const fileInputRef = useRef(null);
  const countdownRef = useRef(null);

  const {
    // State
    isRecording,
    isPaused,
    audioBlob,
    audioURL,
    recordingDuration,
    isTranscribing,
    transcriptionResult,
    recordingConfig,
    
    // Actions
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    transcribeAudio,
    uploadAudioFile,
    setRecordingConfig,
    
    // Utilities
    isSupported,
    formatDuration
  } = useVoiceRecording({ 
    showMessage, 
    onTranscriptionComplete: (result) => {
      if (onTranscriptionComplete) {
        onTranscriptionComplete(result);
      }
      // Automatically send the transcribed message directly to chat
      if (result.transcript && onAutoSendMessage) {
        onAutoSendMessage(result.transcript);
        showMessage('Voice message sent!', 'success');
      }
    }
  });

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('audio/')) {
      showMessage('Please select an audio file', 'error');
      return;
    }

    // Validate file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      showMessage('File too large. Maximum size is 100MB.', 'error');
      return;
    }

    try {
      await uploadAudioFile(file);
    } catch (error) {
      // Error already handled in hook
    }

    // Reset file input
    event.target.value = '';
  };

  // Handle settings change
  const handleSettingsChange = (key, value) => {
    setRecordingConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle insert transcript into input
  const handleInsertTranscript = () => {
    if (transcriptionResult && onInsertText) {
      onInsertText(transcriptionResult.transcript);
      setShowDialog(false);
    }
  };

  // Cleanup countdown timer on unmount or dialog close
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  // Clear countdown when dialog closes
  useEffect(() => {
    if (!showDialog && countdownRef.current) {
      clearInterval(countdownRef.current);
      setAutoSendCountdown(0);
    }
  }, [showDialog]);

  // If not supported, show message
  if (!isSupported()) {
    return (
      <Tooltip title="Voice recording not supported in this browser">
        <span>
          <IconButton disabled>
            <IoMicOff />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  // Compact version for chat input
  if (compact) {
    return (
      <>
        <Tooltip title={isRecording ? "Stop recording" : "Start voice recording"}>
          <IconButton
            size="small"
            onClick={isRecording ? stopRecording : startRecording}
            aria-label={isRecording ? "Stop recording" : "Record voice message"}
            sx={{
              color: isRecording ? "#ef4444" : "#6b7280",
              "&:hover": {
                backgroundColor: "#f3f4f6",
                color: isRecording ? "#dc2626" : "#374151"
              },
              animation: isRecording ? 'recordPulse 1.5s infinite' : 'none',
              '@keyframes recordPulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.6 },
                '100%': { opacity: 1 },
              },
              ...TOUCH_HIT_SX
            }}
          >
            <IoMic size={18} />
          </IconButton>
        </Tooltip>

        {/* Optional: Show recording status overlay */}
        {isRecording && (
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              p: 3,
              borderRadius: 2,
              zIndex: 9999,
              textAlign: 'center',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <IoMic size={18} color="red" />
              <Typography variant="h6">Recording...</Typography>
            </Box>
            <Typography variant="body2" color="grey.300">
              {formatDuration(recordingDuration)}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={stopRecording}
              sx={{ mt: 2, color: 'white', borderColor: 'white' }}
            >
              Stop & Send
            </Button>
          </Box>
        )}

        {/* Show transcribing status */}
        {isTranscribing && (
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              p: 3,
              borderRadius: 2,
              zIndex: 9999,
              textAlign: 'center',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Transcribing...
            </Typography>
            <LinearProgress sx={{ width: 200, mb: 1 }} />
            <Typography variant="body2" color="grey.300">
              Converting speech to text
            </Typography>
          </Box>
        )}
      </>
    );
  }

  // Full component version
  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
      {/* Component content here - can be expanded for full version */}
      <Typography variant="h6" gutterBottom>
        Voice Recording
      </Typography>
      {/* Add full component implementation if needed */}
    </Box>
  );
};

export default VoiceRecordingComponent;
